// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';

import GoffyButton from '@/components/GoffyButton';
import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import { useUser } from '@/app/user-context';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import { createRequestId } from '@/services/requestId';
import {
  FRIEND_INVITE_ROUTE,
  fetchFriendInvitePreview,
  readInviteTokenFromLocation,
  readPendingInviteToken,
  storePendingInviteToken,
} from '@/services/friendInviteLink';
import appColors from '@/theme/colors';

const ERROR_KEY_BY_CODE: Record<string, string> = {
  'friend.invite.expired': 'friendInvite.errorExpired',
  'friend.invite.invalid': 'friendInvite.errorInvalid',
  'friend.invite.self': 'friendInvite.errorSelf',
  'friend.invite.playerRequired': 'friendInvite.errorPlayerRequired',
  'friend.invite.unauthenticated': 'friendInvite.errorUnauthenticated',
};

/** How long to wait for a redeem verdict before calling it a failure. */
const REDEEM_TIMEOUT_MS = 12_000;

/**
 * Landing screen for a scanned friend QR.
 *
 * The token arrives in the URL fragment. It is exchanged immediately — before any sign-in — for a
 * longer-lived pending token, because the scanned token only lives a couple of minutes and a
 * scanner who still has to register would never beat that clock. The exchange also tells us whether
 * the code has already expired, so we can say so before spending a round trip at Keycloak.
 */
export function FriendInviteScreen() {
  const { t } = useI18n();
  const { user, setUser } = useUser();
  const router = useRouter();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);

  const [inviterName, setInviterName] = useState<string | null>(null);
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [connectedName, setConnectedName] = useState<string | null>(null);
  const [alreadyConnected, setAlreadyConnected] = useState(false);
  const redeemedForTokenRef = useRef<string | null>(null);
  const redeemTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRedeemTimeout = useCallback(() => {
    if (redeemTimeoutRef.current) {
      clearTimeout(redeemTimeoutRef.current);
      redeemTimeoutRef.current = null;
    }
  }, []);

  const { handleRedirect, loading: signingIn } = useAuthRedirect({
    user,
    setUser,
    returnTo: FRIEND_INVITE_ROUTE,
  });

  // Resolve the invite once: from the fragment on a fresh scan, or from session storage when we
  // have just come back from Keycloak (the redirect discards the fragment).
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const scannedToken = readInviteTokenFromLocation();
      if (!scannedToken) {
        const stored = readPendingInviteToken();
        if (stored) {
          setPendingToken(stored);
          setLoading(false);
          return;
        }
        setErrorKey('friendInvite.errorInvalid');
        setLoading(false);
        return;
      }

      try {
        const preview = await fetchFriendInvitePreview({ apiBaseUrl, token: scannedToken });
        if (cancelled) {
          return;
        }
        // Persisted before anything can navigate away — the OIDC redirect replaces the whole
        // location, fragment included.
        storePendingInviteToken(preview.pendingToken);
        setPendingToken(preview.pendingToken);
        setInviterName(preview.inviterName || null);
      } catch (err) {
        if (cancelled) {
          return;
        }
        const code = err instanceof Error && 'code' in err ? String((err as { code: string }).code) : '';
        setErrorKey(ERROR_KEY_BY_CODE[code] ?? 'friendInvite.errorFailed');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl]);

  const redeem = useCallback(
    (token: string) => {
      const socket = getSharedBffWebSocket(apiBaseUrl);
      socket.setAuthToken(user?.token ?? null);
      setRedeeming(true);
      // This screen is usually the first thing a scanner loads, so the socket is still opening.
      // send() returns false for a queued frame just as it does for a dropped one, so the return
      // value says nothing about delivery — the client flushes the queue on open. Wait for a
      // verdict instead, and let the timeout be what fails.
      socket.sendFriendInviteRedeem({ token, requestId: createRequestId() });
      clearRedeemTimeout();
      redeemTimeoutRef.current = setTimeout(() => {
        redeemTimeoutRef.current = null;
        setRedeeming(false);
        setErrorKey('friendInvite.errorFailed');
      }, REDEEM_TIMEOUT_MS);
    },
    [apiBaseUrl, clearRedeemTimeout, user?.token]
  );

  useEffect(() => clearRedeemTimeout, [clearRedeemTimeout]);

  // Auto-redeem as soon as we have both a token and a session — including when the session only
  // appears after returning from sign-in. The ref keeps a re-render from redeeming twice.
  useEffect(() => {
    if (!pendingToken || !user?.token || errorKey || connectedName) {
      return;
    }
    if (redeemedForTokenRef.current === pendingToken) {
      return;
    }
    redeemedForTokenRef.current = pendingToken;
    redeem(pendingToken);
  }, [connectedName, errorKey, pendingToken, redeem, user?.token]);

  useEffect(() => {
    const socket = getSharedBffWebSocket(apiBaseUrl);
    const unsubscribe = socket.onMessage((message) => {
      const payload = message as {
        type?: string;
        friendName?: string;
        alreadyConnected?: boolean;
        code?: string;
      };

      if (payload.type === 'friend.invite.redeemed') {
        clearRedeemTimeout();
        setRedeeming(false);
        setAlreadyConnected(!!payload.alreadyConnected);
        setConnectedName(payload.friendName || inviterName || '');
        storePendingInviteToken(null);
        return;
      }

      if (payload.type === 'friend.invite.redeem.rejected') {
        clearRedeemTimeout();
        setRedeeming(false);
        setErrorKey(ERROR_KEY_BY_CODE[payload.code ?? ''] ?? 'friendInvite.errorFailed');
        storePendingInviteToken(null);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [apiBaseUrl, clearRedeemTimeout, inviterName]);

  const renderBody = () => {
    if (loading) {
      return <ActivityIndicator size="large" color={appColors.accent.primary} />;
    }

    if (errorKey) {
      return (
        <>
          <Text style={styles.error} testID="friend-invite-error">
            {t(errorKey)}
          </Text>
          <GoffyButton
            label={t('friendInvite.seeFriends')}
            onPress={() => router.replace('/friends')}
            tone="dark"
            size="compact"
            testID="friend-invite-see-friends"
            style={styles.action}
          />
        </>
      );
    }

    if (connectedName !== null) {
      return (
        <>
          <Text style={styles.status} testID="friend-invite-status">
            {alreadyConnected
              ? t('friendInvite.alreadyConnected', { name: connectedName })
              : t('friendInvite.connected', { name: connectedName })}
          </Text>
          <GoffyButton
            label={t('friendInvite.seeFriends')}
            onPress={() => router.replace('/friends')}
            tone="accent"
            size="compact"
            testID="friend-invite-see-friends"
            style={styles.action}
          />
        </>
      );
    }

    if (!user?.token) {
      return (
        <>
          <Text style={styles.title} testID="friend-invite-status">
            {inviterName
              ? t('friendInvite.previewTitle', { name: inviterName })
              : t('friendInvite.previewUnknown')}
          </Text>
          <GoffyButton
            label={t('friendInvite.signInToConnect')}
            onPress={() => void handleRedirect()}
            disabled={signingIn}
            tone="accent"
            size="compact"
            testID="friend-invite-signin"
            style={styles.action}
          />
        </>
      );
    }

    return (
      <>
        <Text style={styles.title} testID="friend-invite-status">
          {inviterName
            ? t('friendInvite.previewTitle', { name: inviterName })
            : t('friendInvite.previewUnknown')}
        </Text>
        <Text style={styles.subtitle}>{t('friendInvite.connecting')}</Text>
        {redeeming ? <ActivityIndicator size="small" color={appColors.accent.primary} /> : null}
      </>
    );
  };

  return (
    <View style={styles.screen}>
      <View style={styles.card}>{renderBody()}</View>
    </View>
  );
}

export default FriendInviteScreen;

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
    padding: 24,
    gap: 16,
    alignItems: 'center',
  },
  title: {
    color: appColors.text.primary,
    fontSize: 22,
    fontWeight: '800',
    textAlign: 'center',
  },
  subtitle: {
    color: appColors.text.muted,
    fontSize: 14,
    textAlign: 'center',
  },
  status: {
    color: appColors.accent.light,
    fontSize: 20,
    fontWeight: '700',
    textAlign: 'center',
  },
  error: {
    color: appColors.status.errorText,
    fontSize: 16,
    textAlign: 'center',
  },
  action: {
    width: '100%',
  },
});
